// src/components/auth/components/LoginForm.tsx
import React, { useState, useEffect } from 'react';
import { COMPONENT_STYLES } from '@/theme';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/context/AuthContext';
import SocialButtons from './SocialButtons';
import Message from '@/components/ui/Message';
import { useLanguage } from '@/hooks/useLanguage';
import { useResponsive } from '@/hooks/useResponsive';
import styles from './AuthModal.module.css';

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onSwitchToPasswordReset: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister, onSwitchToPasswordReset }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formErrorKey, setFormErrorKey] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { login, loginWithFacebook, loginWithGoogle, isLoading, error: authError } = useAuthContext();
  const { t } = useLanguage();
  useResponsive();

  // Effect for handling authentication errors
  useEffect(() => {
    if (authError) {
      const authErrorStr = typeof authError === 'object' && 'auth' in authError 
        ? String(authError.auth || '') 
        : '';
      
      const apiErrorStr = typeof authError === 'object' && 'api' in authError 
        ? String(authError.api || '') 
        : '';

      setDebugInfo(`${t('common.debug.server_response')}: ${t('common.no')}`);
      
      // Map technical error messages to user-friendly ones using translation keys
      if (apiErrorStr.includes('401') || 
          apiErrorStr.includes('No active account') || 
          apiErrorStr.includes('given credentials') ||
          authErrorStr.includes('Неверные учетные данные')) {
        setFormErrorKey('auth.errors.invalid_credentials');
      } else if (apiErrorStr.includes('timeout') || apiErrorStr.includes('Network Error')) {
        setFormErrorKey('auth.errors.connection_error');
      } else if (authErrorStr) {
        setFormError(authErrorStr);
      } else if (typeof authError === 'string') {
        setFormError(authError);
      }
    }
  }, [authError, t]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDebugInfo(t('common.debug.login_process_start'));
    
    if (!email || !password) {
      setFormErrorKey('auth.errors.required_fields');
      setDebugInfo(t('auth.errors.required_fields'));
      return;
    }
    
    setFormError(null);
    setFormErrorKey(null);
    setDebugInfo(t('common.debug.sending_data'));
    
    try {
      console.log(`${t('common.debug.login_attempt')}: ${email}`);
      const success = await login(email, password);
      
      setDebugInfo(`${t('common.debug.server_response')}: ${success ? t('common.yes') : t('common.no')}`);
      
      if (success) {
        // Redirect to personal cabinet
        navigate('/personal');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      
      // Map technical error messages to user-friendly ones using translation keys
      if (err.message) {
        if (err.message.includes('401') || 
            err.message.includes('No active account') || 
            err.message.includes('given credentials')) {
          setFormErrorKey('auth.errors.invalid_credentials');
        } else if (err.message.includes('timeout') || err.message.includes('Network Error')) {
          setFormErrorKey('auth.errors.connection_error');
        } else {
          setFormErrorKey('auth.errors.general');
        }
      } else {
        setFormErrorKey('auth.errors.general');
      }
      
      setDebugInfo(`${t('common.error')}: ${err.message || t('auth.errors.general')}`);
    }
  };

  return (
    <div className={styles.authForm}>
      {/* Error message using Message component */}
      {formErrorKey && (
        <Message type="error" translateKey={formErrorKey} />
      )}
      
      {formError && !formErrorKey && (
        <Message type="error" message={formError} />
      )}
      
      {/* Debug information - should be removed in production or hidden for regular users */}
      {debugInfo && (
        <Message 
          type="debug" 
          message={
            <>
              <p className="font-bold">{t('common.debug.title')}:</p>
              <p>{debugInfo}</p>
              {authError && typeof authError === 'object' && (
                <>
                  {authError.auth && <p className="mt-1 text-xs">Auth Error: {authError.auth}</p>}
                  {authError.api && <p className="mt-1 text-xs">API Error: {authError.api}</p>}
                </>
              )}
            </>
          } 
        />
      )}
      
      <form onSubmit={handleLoginSubmit} className="space-y-4">
        <div className={styles.formGroup}>
          <label 
            htmlFor="email" 
            className={styles.formLabel}
          >
            {t('auth.common.email')}
          </label>
          <input
            id="email"
            type="text"
            value={email}
            onChange={(e) => setEmail((e.target as any).value)}
            className={COMPONENT_STYLES.input.base}
            placeholder={t('auth.login.email_placeholder') || "auth.login.email_placeholder"}
            disabled={isLoading}
          />
        </div>
        
        <div className={styles.formGroup}>
          <div className={styles.passwordHeader}>
            <label 
              htmlFor="password" 
              className={styles.formLabel}
            >
              {t('auth.common.password')}
            </label>
            <button
              type="button"
              className={styles.forgotPassword}
              onClick={onSwitchToPasswordReset}
            >
              {t('auth.common.forgot_password')}
            </button>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword((e.target as any).value)}
              className={COMPONENT_STYLES.input.base}
              placeholder={t('auth.login.password_placeholder') || "auth.login.password_placeholder"}
              disabled={isLoading}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('auth.common.hide_password') : t('auth.common.show_password')}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        
        <div className={styles.rememberMe}>
          <input
            id="rememberMe"
            type="checkbox"
            checked={rememberMe}
            onChange={() => setRememberMe(!rememberMe)}
            className={styles.rememberMeCheckbox}
          />
          <label 
            htmlFor="rememberMe" 
            className={styles.rememberMeLabel}
          >
            {t('auth.common.remember_me')}
          </label>
        </div>
        
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full ${COMPONENT_STYLES.button.base} ${COMPONENT_STYLES.button.primary}`}
        >
          {isLoading ? t('auth.login.button_loading') : t('auth.login.button')}
        </button>
      </form>
      
      <div className={styles.formDivider}>
        <div className="flex justify-center">
          <span className={styles.formDividerText}>
            {t('auth.common.or_login_with')}
          </span>
        </div>
      </div>
      
      <SocialButtons 
        onFacebookLogin={loginWithFacebook}
        onGoogleLogin={loginWithGoogle}
      />
      
      <div className={styles.formFooter}>
        <p className="text-sm text-[#465357]">
          {t('auth.common.no_account')}{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            className={styles.switchLink}
          >
            {t('auth.login.switch_to_register')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;